'use client'

import { FC, memo, useCallback, useMemo, useState } from 'react'
import Link from 'next/link'
import styled, { css } from 'styled-components'
import { motion, AnimatePresence, type Variants } from 'motion/react'
import { designGrid, mq, presets, vw } from '@/styles'
import { SliderFlickity } from '../../base'
import { LANG } from '@/utils'
import { useDeviceType, useSession } from '@/hooks'
import type { TypeCmsCategory, TypeCmsSlug } from '@/schema'

type Props = {
  categories: TypeCmsCategory[]
  displayPacks?: boolean
}

const getSlug = (slug: string | TypeCmsSlug, logged: boolean): string => {
  const localized = typeof slug === 'string' ? slug : slug[LANG]
  if (localized.includes('packs') && LANG !== 'es') return localized
  if (localized.includes('account')) return localized
  return `/${logged ? 'shop' : 'catalog'}/${localized}`
}

const renderItemList = ({ data, onMouseOver, triggerDropdown = true, setCategoryBeingHovered, logged }) =>
  data.map((item, idx) => {
    const slug = getSlug(item.slug, logged)
    const name = typeof item.name === 'string' ? item.name : item.name[LANG]

    const handleMouseOver = () => {
      if (triggerDropdown) {
        setCategoryBeingHovered?.(slug)
        onMouseOver?.(idx + 1, item.subcategories)
      }
    }

    return (
      <Li key={`${idx}-${slug}`} className={triggerDropdown ? 'bar' : ''} onMouseOver={handleMouseOver}>
        <Link href={slug}>{name}</Link>
      </Li>
    )
  })

export const MenuSlider:FC<Props> = memo(({ categories, displayPacks = false }) => {
  const { user } = useSession()
  const { isMobile } =  useDeviceType()
  const [categoryBeingHovered, setCategoryBeingHovered] = useState(null)
  const [childVisible, setChildVisible] = useState(null)
  const [dropdownSubcategories, setDropdownSubcategories] = useState([])

  const updatedCategories = useMemo(() => {
    return [
      ...(!!user ? [myUsualAsLink] : []),
      ...categories,
      ...(displayPacks && LANG !== 'es' ? [packsAsLink] : [])
    ]
  }, [user, categories, displayPacks])

  const handleMouseOver = useCallback((position, subcategories) => {
    if (!position || !subcategories?.length) {
      setChildVisible(null)
      setDropdownSubcategories([])
    } else {
      setChildVisible(position)
      setDropdownSubcategories(subcategories)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setChildVisible(null)
    setDropdownSubcategories([])
    setCategoryBeingHovered(null)
  }, [])

  const Slides = useMemo(() => renderItemList({ data: updatedCategories, onMouseOver: handleMouseOver, setCategoryBeingHovered, logged: !!user }), [handleMouseOver, updatedCategories, user])
  const DropdownItems = useMemo(() => renderItemList({ data: dropdownSubcategories, onMouseOver: null, triggerDropdown: false, setCategoryBeingHovered: null, logged: !!user }), [dropdownSubcategories, user])

  if(isMobile) return null
  return (
    <Ul onMouseLeave={handleMouseLeave} $categoryBeingHovered={categoryBeingHovered} $childVisible={childVisible}>
      <SliderFlickity className='slider--menu' options={{ cellAlign: 'left', autoPlay: false, prevNextButtons: false, imagesLoaded: true, draggable: true, wrapAround: false, contain: true, groupCells: false }} dynamicSlider={true}>
        {Slides}
      </SliderFlickity>
      <AnimatePresence>
        {childVisible && (
          <LiDropdown key='subcategories' variants={dropdownVariants} initial='initial' animate='animate' exit='exit'>
            <ul>{DropdownItems}</ul>
          </LiDropdown>
        )}
      </AnimatePresence>
    </Ul>
  )
})


const Ul = styled.ul<{ $categoryBeingHovered: string, $childVisible: number }>`
  display: none;

  ${mq.greaterThan('tablet')} {
    ${designGrid({})}
    margin: ${vw(30, 'desktop')} 0 0;
		overflow-x: clip;
    padding-bottom: ${vw(40, 'desktop')};
    position: relative;
    width: 100vw;
    width: 100dvw;
  }

  ${mq.greaterThan('container')} {
    max-width: var(--page-container-max-width);
    margin: 30px auto 0;
    padding-bottom: 40px;
  }

  .slider-wrapper {
    grid-column: 1 / span 12;
  }

  .bar {
    ${({ $categoryBeingHovered }) => $categoryBeingHovered && css`
      a {
        opacity: .65;

        &:hover {
          opacity: 1;
          transform: scale(1.04) translateY(-1px);
        }
      }
    `}


    ${({ $childVisible }) => $childVisible && css`
      &:nth-child(${$childVisible}) {
        color: var(--color-gold);

        > a {
          opacity: 1 !important;
        }
      }
    `}
  }
`
const Li = styled(motion.li)`
  ${mq.greaterThan('tablet')} {
    ${presets.font18_4}
    color: var(--color-text);
    font-family: var(--font-platform);
    display: inline-block;
    height: ${vw(22, 'desktop')};
    margin-right: ${vw(30, 'desktop')};
    transition: 200ms color ease-out;
    width: max-content;
  }


  &.bar {
    &:nth-last-child(-n + 1)  {
      padding-right: ${vw(55, 'desktop')};

      ${mq.greaterThan('container')}{
        padding-right: 55px;
      }
    }
  }

  a {
    display: inline-block;
    opacity: 1;
    transition: 250ms all linear;

    &:hover {
      opacity: 1;
    }
  }
`

const LiDropdown = styled(motion.li)`
  background: var(--color-background);
  min-height: 60px;
  padding: ${vw(30, 'desktop')} ${vw(30, 'desktop')} ${vw(40, 'desktop')};
  position: absolute;
  width: 100vw;
  z-index: 10;
  top: 20px;

  ul {
    display: flex;
    flex-wrap: wrap;
  }

  li {
    flex-shrink: 0;
    height: 25px;
    margin: 0 0 ${vw(8, 'desktop')};

    ${mq.greaterThan('desktop')} {
      margin: 0 0 8px;
    }

    &:last-child {
      margin-bottom: 0;
    }

    > a {
      display: block;
      font-weight: normal;
      height: 25px;
      opacity: .7;
      transition: 200ms opacity ease-out;
      width: 100%;

      &:hover {
        opacity: 1;
      }
    }

    ${mq.greaterThan('tablet')} {
      padding: 0 ${vw(5, 'desktop')};
      width: ${100 / 3}%;
    }


    ${mq.greaterThan('desktop-md')} {
      width: 25%;
    }
  }
`



const TRANSLATIONS = {
  de: {
    name: 'Meine Favoriten'
  },
  en: {
    name: 'My Favourites'
  },
  es: {
    name: 'Mis habituales'
  },
  fr: {
    name: 'Mon historique'
  },
  pt: {
    name: 'Meus habituais'
  }
}


export const myUsualAsLink = {
  id: 666669,
  categoryType: 'custom',
  name: TRANSLATIONS[LANG].name,
  slug: {
    de: '/account/my-products',
    es: '/account/my-products',
    en: '/account/my-products',
    fr: '/account/my-products',
    pt: '/account/my-products'
  },
  subcategories: []
}

export const packsAsLink = {
  id: 666668,
  categoryType: 'product',
  name: 'Packs',
  slug: {
    de: '/packs',
    es: '/packs',
    en: '/packs',
    fr: '/packs',
    pt: '/packs'
  },
  subcategories: []
}

const transition = {
  ease: [.25, .1, .25, 1],
  duration: .2,
  type: 'tween'
} as const

const dropdownVariants:Variants = {
  initial: { opacity: 0, y: '-5px', transition },
  animate: { opacity: 1, y: 0, transition },
  exit: { opacity: 0, y: '-5px', transition }
}
